import React from "react";

import { useNavigate, useTranslation } from "utils";

import { SOILSIGHT_LANDING_STEP, SOILSIGHT_STEP_ROUTE } from "consts";

import { Button, Icon, LazyLoadImage, Typography } from "components";

import { PreLoginHeroGraphic } from "pages/app/soilsight-pre-login/components/PreLoginHeroGraphic";
import { trackSoilSightSignupClick } from "pages/app/soilsight-pre-login/utils/acquisitionTracking";
import { useSkipAcquisitionFlow } from "pages/app/soilsight-pre-login/utils/useSkipAcquisitionFlow";

type StartSimulationProps = {
  onStartSimulation: () => void;
};

const RISK_CARDS = [
  { key: "pressure", iconName: "tabler/cloud", imageSrc: "/images/soilsight-pre-login/risk-pressure.jpg" },
  { key: "resilience", iconName: "klim/crop-rotation", imageSrc: "/images/soilsight-pre-login/risk-resilience.jpg" },
  { key: "peace", iconName: "klim/heart-handshake", imageSrc: "/images/homepage/homepage-banner.jpg" },
] as const;

const FORECAST_CARDS = [
  { key: "soil", iconName: "klim/crops-emerging" },
  { key: "agronomy", iconName: "tabler/cloud" },
  { key: "farmers", iconName: "klim/field" },
  { key: "personal", iconName: "klim/crop-rotation" },
] as const;

const TRUST_STAT_KEYS = ["partners", "countries", "hectares", "farmers"] as const;

const HeroSection = ({ onStartSimulation }: StartSimulationProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  return (
    <section className="soilsight-pre-login__hero" aria-labelledby="soilsight-pre-login-hero-title">
      <div className="soilsight-pre-login__hero-inner">
        <div className="soilsight-pre-login__hero-copy">
          <div className="soilsight-pre-login__hero-text">
            <Typography variant="paragraph-xs" className="soilsight-pre-login__eyebrow">
              {t("preLogin.landing.hero.eyebrow")}
            </Typography>
            <div className="soilsight-pre-login__hero-headlines">
              <Typography variant="hero" id="soilsight-pre-login-hero-title" className="soilsight-pre-login__hero-title">
                {t("preLogin.landing.hero.title")}
              </Typography>
              <Typography variant="paragraph-l" className="soilsight-pre-login__hero-subtitle">
                {t("preLogin.landing.hero.subtitle")}
              </Typography>
            </div>
          </div>
          <Button type="primary" dataTestId="soilsight-pre-login-cta" className="soilsight-pre-login__cta" onClick={onStartSimulation}>
            {t("preLogin.landing.hero.cta")}
          </Button>
        </div>
        <div className="soilsight-pre-login__hero-media">
          <PreLoginHeroGraphic />
        </div>
      </div>
    </section>
  );
};

const RiskSection = ({ onStartSimulation }: StartSimulationProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  return (
    <section className="soilsight-pre-login__risk" aria-labelledby="soilsight-pre-login-risk-title">
      <div className="soilsight-pre-login__section-header">
        <Typography variant="h1" id="soilsight-pre-login-risk-title" className="soilsight-pre-login__section-title">
          {t("preLogin.landing.risk.title")}
        </Typography>
        <Typography variant="paragraph-l" className="soilsight-pre-login__section-subtitle">
          {t("preLogin.landing.risk.subtitle")}
        </Typography>
      </div>
      <ul className="soilsight-pre-login__risk-cards">
        {RISK_CARDS.map(({ key: cardKey, iconName, imageSrc }) => (
          <li key={cardKey} className="soilsight-pre-login__risk-card">
            <div className="soilsight-pre-login__risk-image-wrap">
              <LazyLoadImage
                src={imageSrc}
                alt={t(`preLogin.landing.risk.cards.${cardKey}.imageAlt`)}
                className="soilsight-pre-login__risk-image"
                width="100%"
                height="100%"
              />
            </div>
            <div className="soilsight-pre-login__risk-card-copy">
              <div className="soilsight-pre-login__risk-card-heading">
                <span className={`soilsight-pre-login__card-icon soilsight-pre-login__card-icon--${cardKey}`} aria-hidden="true">
                  <Icon iconName={iconName} size="m-light" />
                </span>
                <Typography variant="h4" className="soilsight-pre-login__risk-card-title">
                  {t(`preLogin.landing.risk.cards.${cardKey}.title`)}
                </Typography>
              </div>
              <Typography variant="paragraph-l">{t(`preLogin.landing.risk.cards.${cardKey}.body`)}</Typography>
            </div>
          </li>
        ))}
      </ul>
      <Button type="primary" dataTestId="soilsight-pre-login-cta-risk" className="soilsight-pre-login__cta" onClick={onStartSimulation}>
        {t("preLogin.landing.hero.cta")}
      </Button>
    </section>
  );
};

const ForecastSection = () => {
  const { t } = useTranslation("scenarioAnalysis");

  return (
    <section className="soilsight-pre-login__forecast" aria-labelledby="soilsight-pre-login-forecast-title">
      <div className="soilsight-pre-login__section-header soilsight-pre-login__section-header--tight">
        <Typography variant="h2" id="soilsight-pre-login-forecast-title" className="soilsight-pre-login__section-title" color="white">
          {t("preLogin.landing.forecast.title")}
        </Typography>
        <Typography variant="paragraph-l" color="white" className="soilsight-pre-login__section-subtitle">
          {t("preLogin.landing.forecast.subtitle")}
        </Typography>
      </div>
      <ul className="soilsight-pre-login__forecast-cards">
        {FORECAST_CARDS.map(({ key: cardKey, iconName }) => (
          <li key={cardKey} className="soilsight-pre-login__forecast-card">
            <span className="soilsight-pre-login__forecast-icon" aria-hidden="true">
              <Icon iconName={iconName} size="m" />
            </span>
            <div className="soilsight-pre-login__forecast-card-copy">
              <Typography variant="h5">{t(`preLogin.landing.forecast.cards.${cardKey}.title`)}</Typography>
              <Typography variant="paragraph-m">{t(`preLogin.landing.forecast.cards.${cardKey}.body`)}</Typography>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

const TrustSection = () => {
  const { t } = useTranslation("scenarioAnalysis");

  return (
    <section className="soilsight-pre-login__trust" aria-labelledby="soilsight-pre-login-trust-title">
      <Typography variant="h2" id="soilsight-pre-login-trust-title" className="soilsight-pre-login__section-title">
        {t("preLogin.landing.trust.title")}
      </Typography>
      <ul className="soilsight-pre-login__trust-stats">
        {TRUST_STAT_KEYS.map((statKey) => (
          <li key={statKey} className={`soilsight-pre-login__stat soilsight-pre-login__stat--${statKey}`}>
            <div className="soilsight-pre-login__stat-copy">
              <Typography variant="h1" className="soilsight-pre-login__stat-value">
                {t(`preLogin.landing.trust.stats.${statKey}.value`)}
              </Typography>
              <Typography variant="paragraph-l" color="grey-600" className="soilsight-pre-login__stat-label">
                {t(`preLogin.landing.trust.stats.${statKey}.label`)}
              </Typography>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export const PreLoginLandingBody = () => {
  const navigate = useNavigate();

  const { isSkipEnabled } = useSkipAcquisitionFlow();

  // TODO(KH-1131): remove the signup shortcut with the skip-acquisition-flow flag.
  const startSimulation = () => {
    if (!isSkipEnabled) {
      navigate(SOILSIGHT_STEP_ROUTE("location"));
      return;
    }
    trackSoilSightSignupClick(SOILSIGHT_LANDING_STEP);
    navigate(SOILSIGHT_STEP_ROUTE("signup"));
  };

  return (
    <div className="soilsight-pre-login__landing">
      <HeroSection onStartSimulation={startSimulation} />
      <RiskSection onStartSimulation={startSimulation} />
      <ForecastSection />
      <TrustSection />
    </div>
  );
};
